import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import type { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Offcanvas, {
	OffcanvasButton,
} from 'tbk-components/src/components/Offcanvas';
import Image from 'tbk-components/src/components/Image';
import Nav, { NavItem } from 'tbk-components/src/components/Nav';
import Button, { ILink } from 'tbk-components/src/components/Button';
import Icon from 'tbk-components/src/components/Icon';
import AlertBarModified from '../AlertBarModified';

// reuse IconLink interface from linkGroup
import type { IIconLink } from '../LinkGroup';
import logoText from '../../assets/logo-text.svg';
import NavMobile from './NavMobile';
import SearchTool from '../SearchTool';
import SocialIcons, { SocialIcon } from '../SocialIcons';

export interface IHeaderProps extends IBaseElementProps {
	logoURL?: string;
	logoAlt?: string;
	isSticky?: boolean;
	homeURL?: string;
	topRowMenu?: NavItem[];
	bottomRowMenu?: NavItem[];
	loginMenu?: NavItem[];
	mobileMenu?: NavItem[];
	secondaryMenu?: NavItem[];
	mainCtaButton?: IIconLink;
	secondaryCtaButton?: IIconLink;
	alertLink?: any;
	socialIcons?: SocialIcon[];
}

/**
 * @requiresHooks
 */

const Header: React.FC<IHeaderProps> = ({
	classNames = [],
	className,
	alertLink,
	logoURL,
	logoAlt,
	isSticky = true,
	homeURL,
	topRowMenu,
	bottomRowMenu,
	loginMenu,
	mobileMenu,
	secondaryMenu,
	mainCtaButton,
	secondaryCtaButton,
	socialIcons,
	...props
}) => {
	const [showSearch, setShowSearch] = useState(false);
	const ref = useRef(null);

	// component to be passed to SearchTool component
	const searchInput = (props) => (
		<>
			<button
				className="search__submit"
				type="submit"
				aria-label="Submit search"
			>
				<Icon name="search" />
			</button>
			<input
				{...props}
				className="search__input search__input--btn-left"
				type="search"
				placeholder="Search"
				aria-label="Search"
				name="s"
				autoFocus
			/>
		</>
	);

	// Helper class name for sticky positioning
	useEffect(() => {
		if (!ref.current || isSticky !== true) {
			return;
		}
		const navbar = ref.current! as HTMLDivElement;
		const topOffset = navbar.offsetTop;
		const handleScroll = () => {
			if (window.scrollY > topOffset) {
				document.body.classList.add('navbar-sticky');
				document.body.style.paddingTop = `${navbar.clientHeight}px`;
			} else {
				document.body.classList.remove('navbar-sticky');
				document.body.style.paddingTop = '';
			}
		};

		const hideSearchInput = (e:any) => {
			// blacklist of possible classes that should not trigger this
			const classNames = ['search', 'search-btn', 'search-toggle', 'search__input', 'icon-search'];
			if (!classNames.some((className) => e.target.classList.contains(className))) {
				setShowSearch(false);
			}
		};

		window.addEventListener('scroll', handleScroll);
		window.addEventListener('click', hideSearchInput);

		return () => {
			window.removeEventListener('scroll', handleScroll);
			window.removeEventListener('click', hideSearchInput);
		};
	}, []);

	return (
		<>
			<nav className="navbar-wrapper" ref={ref}>
				<div
					className={
						className ||
						cn('navbar', 'd-none', 'd-lg-block', ...classNames)
					}
				>
					{alertLink.length > 0 &&
						alertLink.map((alertBar, index) => {
							return (
								alertBar?.children && (
									<AlertBarModified
										key={index}
										classNames={['navbar__alert']}
										alertHash={alertBar.alertHash}
										isClosable={alertBar.isClosable}
										indexNum={index}
									>
										<div>{alertBar.children}</div>
									</AlertBarModified>
								)
							);
						})}
					<div className="navbar__inner">
						<div className="navbar__left">
							<a className="navbar-brand" href={logoURL || '/'}>
								<Image
									src={logoText}
									width={160}
									height={74}
									alt={logoAlt}
								/>
							</a>
						</div>
						<div className="navbar__right">
							<div className="navbar__right-top d-lg-none d-xl-flex">
								{topRowMenu?.length > 0 && (
									<Nav navItems={topRowMenu} />
								)}
								{secondaryCtaButton?.label && (
									<Button
										{...secondaryCtaButton}
										classNames={['cta-btn']}
										type="primary"
										size="sm"
									/>
								)}
							</div>

							<div className="navbar__right-bottom">
								<div className="navbar__right-bottom-menus">
									{bottomRowMenu?.length > 0 && (
										<Nav navItems={bottomRowMenu} />
									)}
									{loginMenu?.length > 0 && (
										<Nav
											navItems={[
												{
													href: '#',
													label: 'Portal Login',
													children: loginMenu,
												},
											]}
										/>
									)}
								</div>
								<div id="google_translate_element" />
								<div className="navbar__right-bottom-buttons">
									{mainCtaButton?.label && (
										<Button
											{...mainCtaButton}
											classNames={[
												'cta-btn',
												showSearch &&
													'd-lg-none d-xl-flex', // hide on large tablet/small laptop screens only
											]}
											type="primary"
											size="sm"
										/>
									)}
									<div
										className={cn(
											'search',
											showSearch && 'expand',
										)}
									>
										{showSearch && (
											<form action={homeURL}>
												<SearchTool
													renderInput={searchInput}
												/>
											</form>
										)}
										{!showSearch && (
											<button
												className={cn(
													'search-btn',
													'search-toggle',
												)}
												type="button"
												aria-label="show search bar"
												onClick={() => {
													setShowSearch(true);
												}}
											>
												<Icon name="search" />
											</button>
										)}
									</div>
									<OffcanvasButton
										className="btn offcanvas-btn"
										target=".offcanvas-secondary-menu"
									>
										<Icon name="menu" />
									</OffcanvasButton>
								</div>
							</div>
						</div>
					</div>
				</div>
				<NavMobile
					alertLink={alertLink}
					mobileMenu={mobileMenu}
					loginMenu={loginMenu}
					secondaryMenu={secondaryMenu}
					ctaButton={mainCtaButton}
					homeURL={homeURL}
					socialIcons={socialIcons}
				/>
			</nav>
			<Offcanvas classNames={['offcanvas-secondary-menu']}>
				{secondaryCtaButton?.label && (
					<Button
						{...secondaryCtaButton}
						classNames={['cta-btn', 'd-xl-none']}
						type="secondary"
						size="sm"
					/>
				)}
				{topRowMenu?.length > 0 && (
					<Nav
						navItems={topRowMenu}
						classNames={['top-nav-menu', 'd-xl-none']}
						vertical
						collapseEnabled
						dropdownEnabled={false}
					/>
				)}
				{secondaryMenu?.length > 0 && (
					<Nav
						navItems={secondaryMenu}
						classNames={['secondary-menu']}
						vertical
						collapseEnabled
						dropdownEnabled={false}
					/>
				)}
				{socialIcons && (
					<SocialIcons
						showFollowUs
						showLogo={false}
						icons={socialIcons}
					/>
				)}
			</Offcanvas>
		</>
	);
};

export default Header;
